import { ChangeDetectionStrategy, Component, ElementRef, HostListener, Inject, Input, OnDestroy } from '@angular/core';
import { Subject, timer } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { ElCdkFooterRow, ElCdkHeaderRow, ElCdkRow } from '../cdk/table/type-mappings';
import { ElFooterRowComponent, ElHeaderRowComponent, ElRowComponent } from '../cdk/table/row';
import { ElTreeGridComponent } from './tree-grid.component';
import { EL_TREE_GRID } from './tree-grid-injection-tokens';

export const EL_ROW_DOUBLE_CLICK_DELAY: number = 200;

/**
 * Cells container. Adds the right class and role.
 */
@Component({
  selector: 'tr[elTreeGridRow]',
  template: `<ng-container elCellOutlet></ng-container>`,
  host: {
    'class': 'el-tree-grid-row',
    'role': 'row',
  },
  providers: [{ provide: ElCdkRow, useExisting: ElTreeGridRowComponent }],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ElTreeGridRowComponent extends ElRowComponent implements OnDestroy {
  private readonly doubleClick$ = new Subject();
  private readonly tree: ElTreeGridComponent<any>;

  /**
   * Time to wait for second click to expand row deeply.
   * 200ms by default.
   */
  @Input() doubleClickDelay: number = EL_ROW_DOUBLE_CLICK_DELAY;

  /**
   * Toggle row on click. Enabled by default.
   */
  @Input() clickToToggle: boolean = true;

  @HostListener('click')
  toggleIfEnabledNode(): void {
    if (!this.clickToToggle) {
      return;
    }

    timer(EL_ROW_DOUBLE_CLICK_DELAY)
      .pipe(
        take(1),
        takeUntil(this.doubleClick$),
      )
      .subscribe(() => this.tree.toggleRow(this));
  }

  @HostListener('dblclick')
  toggleIfEnabledNodeDeep() {
    if (!this.clickToToggle) {
      return;
    }

    this.doubleClick$.next();
    this.tree.toggleRow(this, { deep: true });
  }

  constructor(
    @Inject(EL_TREE_GRID) tree,
    public elementRef: ElementRef<HTMLElement>,
  ) {
    super();
    this.tree = tree as ElTreeGridComponent<any>;
  }

  ngOnDestroy() {
    this.doubleClick$.complete();
  }
}

@Component({
  selector: 'tr[elTreeGridHeaderRow]',
  template: `
    <ng-container elCellOutlet></ng-container>`,
  host: {
    'class': 'el-tree-grid-header-row',
    'role': 'row',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: ElCdkHeaderRow, useExisting: ElTreeGridHeaderRowComponent }],
})
export class ElTreeGridHeaderRowComponent extends ElHeaderRowComponent { }

@Component({
  selector: 'tr[elTreeGridFooterRow]',
  template: `
    <ng-container elCellOutlet></ng-container>`,
  host: {
    'class': 'el-tree-grid-footer-row',
    'role': 'row',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: ElCdkFooterRow, useExisting: ElTreeGridFooterRowComponent }],
})
export class ElTreeGridFooterRowComponent extends ElFooterRowComponent { }
